import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@mui/material';

import { SpaceID } from '@playq/octopus-common';
import { Space } from '@playq/octopus2-apps';
import { SpacePromotions } from '@playq/octopus2-apps-utility';

import { SelectDialog } from '/shared/SelectDialog';
import { CollectionTableAnyFilterDetached, CollectionTableColumnSelectionDetached } from '/shared/Table';

import { ISpacesListProps } from './types';
import { useSpacesList } from './useSpacesList';
import { SpacesTable } from './components/SpacesTable';
import { SpaceDeletion } from './components/SpaceDeletion';
import { SpacesColumnType } from './components/types';
import { actionsMenuCSS, actionsRowCellControlsCSS, ListActionsWrapper } from './styles';
import { useSpacesTableActions } from './components/SpacesTableActions';

export const SpacesList: FC<ISpacesListProps> = (props) => {
  const { onSelect, onCreate, onEdit, initialQuery, onClose, activeSpaceID } = props;
  const [spacePromotions, setSpacePromotions] = useState<SpacePromotions | undefined>();
  const [currentSpaceID, setCurrentSpaceID] = useState<SpaceID | undefined>();
  const [columns, setColumns] = useState<SpacesColumnType[]>([]);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [enableQuerySpaces, setEnableQuerySpaces] = useState<boolean>(true);
  const didCancel = useRef(false);

  const {
    spaces,
    pending,
    total,
    canWrite,
    entitiesProcessing,
    query,
    spacesQueryKey,
    features,

    setQuery,
    deleteSpace,
    refresh,
  } = useSpacesList(initialQuery, { enabled: enableQuerySpaces });

  useEffect(
    () => () => {
      didCancel.current = true;
    },
    []
  );

  const handleUseDefault = () => onSelect();

  const handleCreate = () => onCreate();

  const handleEdit = (space: Space) => onEdit(space, spacesQueryKey);

  const submitSpaceDeletion = useCallback(
    (spaceID: SpaceID) => {
      setSpacePromotions(undefined);
      setCurrentSpaceID(undefined);
      deleteSpace(spaceID);
    },
    [deleteSpace]
  );

  const isHidden = useCallback((label: string) => hiddenColumns.includes(label), [hiddenColumns]);

  const onSetColumns = useCallback((newColumns: SpacesColumnType[]) => setColumns(newColumns), []);

  const { actions, PromotionsEntitiesPopover } = useSpacesTableActions({
    setCurrentSpaceID,
    setSpacePromotions,
  });

  return (
    <SelectDialog
      title='Select Space'
      open={true}
      onClose={onClose}
      headerAction={
        <>
          <CollectionTableAnyFilterDetached query={query} processing={pending} setQuery={setQuery} />
          <CollectionTableColumnSelectionDetached
            columns={columns}
            hiddenColumns={hiddenColumns}
            setHiddenColumns={setHiddenColumns}
          />
        </>
      }
      actions={
        <ListActionsWrapper>
          <Button variant='contained' onClick={handleCreate} data-testid='button-create'>
            Create
          </Button>
          <Button variant='contained' onClick={handleUseDefault} color='primary'>
            Use Default
          </Button>
        </ListActionsWrapper>
      }
      content={
        <>
          <SpacesTable
            features={features}
            entities={spaces}
            entitiesProcessing={entitiesProcessing}
            activeSpaceID={activeSpaceID}
            total={total}
            query={query}
            pending={pending}
            canWrite={canWrite}
            queryKeys={spacesQueryKey}
            actions={actions}
            styles={{ actionsMenu: actionsMenuCSS, actionsRowCellControls: actionsRowCellControlsCSS }}
            setEnableQuerySpaces={setEnableQuerySpaces}
            onSelectSpace={onSelect}
            onQueryChange={setQuery}
            onEditSpace={handleEdit}
            refetch={refresh}
            isHidden={isHidden}
            onSetColumns={onSetColumns}
            onClose={onClose}
          />
          {currentSpaceID && spacePromotions && (
            <SpaceDeletion spaceID={currentSpaceID} spacePromotions={spacePromotions} onDelete={submitSpaceDeletion} />
          )}
          {PromotionsEntitiesPopover}
        </>
      }
    />
  );
};
